import React, { useState, useEffect, useCallback } from 'react';
// Apne constants file ka sahi path yahan dalein
import { API_URL } from "../../Constants";

// IMPORTANT: this must match the `google.oauth.frontend-origin` value set in
// the backend's application.properties, and must be an exact origin
// (scheme + host + port), never "*".
const FRONTEND_ORIGIN = window.location.origin;

const LEAD_FIELD_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'phone', label: 'Phone' },
  { value: 'email', label: 'Email' },
  { value: 'city', label: 'City' },
  { value: 'state', label: 'State' },
  { value: 'address', label: 'Address' },
  { value: 'comment', label: 'Comment' },
  { value: 'companyname', label: 'Company Name' },
  { value: 'reference', label: 'Reference' },
  { value: 'othercontactno', label: 'Other Contact No' },
];

const QUESTION_TYPES = [
  { value: 'SHORT_ANSWER', label: 'Short answer' },
  { value: 'PARAGRAPH', label: 'Paragraph' },
  { value: 'MULTIPLE_CHOICE', label: 'Multiple choice (radio)' },
  { value: 'CHECKBOX', label: 'Checkboxes' },
  { value: 'DROPDOWN', label: 'Dropdown' },
  { value: 'NUMERIC', label: 'Number' },
];

const NEEDS_OPTIONS = new Set(['MULTIPLE_CHOICE', 'CHECKBOX', 'DROPDOWN']);

function blankQuestion() {
  return { title: '', required: true, type: 'SHORT_ANSWER', options: [''], leadFieldMapping: '' };
}

/** Turns our internal question shape into the payload the backend expects */
function serializeQuestions(questions) {
  return questions.map((q) => ({
    title: q.title,
    required: q.required,
    type: q.type,
    options: NEEDS_OPTIONS.has(q.type) ? q.options.filter((o) => o.trim()) : null,
    leadFieldMapping: q.leadFieldMapping || null,
  }));
}

/**
 * Turns Google's live `items` array (from GET /{configId}/details) plus this
 * form's saved questionMappings back into our internal question shape, so
 * the edit builder can be pre-filled with what the form actually looks like
 * right now.
 */
function deserializeQuestions(items, questionMappings) {
  const mappings = questionMappings || {};
  if (!Array.isArray(items) || items.length === 0) return [blankQuestion()];

  return items.map((item) => {
    const question = item?.questionItem?.question || {};
    const questionId = question.questionId;
    const mapping = questionId ? mappings[questionId] : null;

    let type = 'SHORT_ANSWER';
    let options = [''];

    if (question.choiceQuestion) {
      const cType = question.choiceQuestion.type;
      type = cType === 'RADIO' ? 'MULTIPLE_CHOICE' : cType === 'CHECKBOX' ? 'CHECKBOX' : 'DROPDOWN';
      const rawOptions = (question.choiceQuestion.options || []).map((o) => o.value || '');
      options = rawOptions.length ? rawOptions : [''];
    } else if (question.textQuestion) {
      if (question.textQuestion.paragraph) {
        type = 'PARAGRAPH';
      } else if (mapping?.type === 'NUMERIC') {
        // Google stores NUMERIC as a plain text question; the distinction
        // only survives in our own saved lead-field mapping.
        type = 'NUMERIC';
      } else {
        type = 'SHORT_ANSWER';
      }
    }

    return {
      title: item.title || '',
      required: !!question.required,
      type,
      options,
      leadFieldMapping: mapping?.leadField || '',
    };
  });
}

/**
 * Shared editing logic for a `{ questions: [...] }`-shaped piece of state.
 * Used for both the "create form" builder and the "edit form" builder so
 * the two don't drift out of sync.
 */
function createQuestionHandlers(setForm) {
  const updateQuestion = (idx, patch) => {
    setForm((f) => {
      const questions = [...f.questions];
      questions[idx] = { ...questions[idx], ...patch };
      return { ...f, questions };
    });
  };

  const updateOption = (qIdx, optIdx, value) => {
    setForm((f) => {
      const questions = [...f.questions];
      const options = [...questions[qIdx].options];
      options[optIdx] = value;
      questions[qIdx] = { ...questions[qIdx], options };
      return { ...f, questions };
    });
  };

  const addOption = (qIdx) => {
    setForm((f) => {
      const questions = [...f.questions];
      questions[qIdx] = { ...questions[qIdx], options: [...questions[qIdx].options, ''] };
      return { ...f, questions };
    });
  };

  const removeOption = (qIdx, optIdx) => {
    setForm((f) => {
      const questions = [...f.questions];
      const options = questions[qIdx].options.filter((_, i) => i !== optIdx);
      questions[qIdx] = { ...questions[qIdx], options };
      return { ...f, questions };
    });
  };

  const addQuestion = () => setForm((f) => ({ ...f, questions: [...f.questions, blankQuestion()] }));
  const removeQuestion = (idx) =>
    setForm((f) => ({ ...f, questions: f.questions.filter((_, i) => i !== idx) }));

  return { updateQuestion, updateOption, addOption, removeOption, addQuestion, removeQuestion };
}

/** Securely decode the JWT payload and pull the COMPANY id (not the user id) */
function getCompanyIdFromToken(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window.atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const decodedToken = JSON.parse(jsonPayload);
    
    // Yahan decodedToken.companyId ki jagah decodedToken.id kar diya hai
    return decodedToken.id || null;
  } catch (err) {
    console.error('Failed to decode token payload:', err);
    return null;
  }
}

function authHeaders() {
  const token = localStorage.getItem('token');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

/** The question-list editor used by both the create and edit builders */
const QuestionListEditor = ({ questions, handlers }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <label className="block text-sm font-medium text-gray-700">Questions</label>
      <button onClick={handlers.addQuestion} className="text-sm text-[#673AB7] hover:underline">+ Add question</button>
    </div>

    <div className="space-y-4">
      {questions.map((q, idx) => (
        <div key={idx} className="border border-gray-200 rounded-md p-3">
          <div className="flex items-center gap-2 mb-2">
            <input
              type="text"
              className="flex-1 border border-gray-300 rounded-md px-2 py-1.5 text-sm"
              placeholder="Question text"
              value={q.title}
              onChange={(e) => handlers.updateQuestion(idx, { title: e.target.value })}
            />
            {questions.length > 1 && (
              <button onClick={() => handlers.removeQuestion(idx)} className="text-xs text-red-500 hover:underline">Remove</button>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2 mb-2">
            <select
              className="border border-gray-300 rounded-md px-2 py-1.5 text-sm"
              value={q.type}
              onChange={(e) => handlers.updateQuestion(idx, { type: e.target.value })}
            >
              {QUESTION_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>

            <select
              className="border border-gray-300 rounded-md px-2 py-1.5 text-sm"
              value={q.leadFieldMapping}
              onChange={(e) => handlers.updateQuestion(idx, { leadFieldMapping: e.target.value })}
            >
              <option value="">Don't map to a lead field</option>
              {LEAD_FIELD_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>Map to: {o.label}</option>
              ))}
            </select>
          </div>

          <label className="flex items-center gap-2 text-xs text-gray-600 mb-2">
            <input
              type="checkbox"
              checked={q.required}
              onChange={(e) => handlers.updateQuestion(idx, { required: e.target.checked })}
            />
            Required
          </label>

          {NEEDS_OPTIONS.has(q.type) && (
            <div className="space-y-1">
              {q.options.map((opt, optIdx) => (
                <div key={optIdx} className="flex items-center gap-2">
                  <input
                    type="text"
                    className="flex-1 border border-gray-300 rounded-md px-2 py-1 text-sm"
                    placeholder={`Option ${optIdx + 1}`}
                    value={opt}
                    onChange={(e) => handlers.updateOption(idx, optIdx, e.target.value)}
                  />
                  {q.options.length > 1 && (
                    <button onClick={() => handlers.removeOption(idx, optIdx)} className="text-xs text-red-500">✕</button>
                  )}
                </div>
              ))}
              <button onClick={() => handlers.addOption(idx)} className="text-xs text-[#673AB7] hover:underline">+ Add option</button>
            </div>
          )}
        </div>
      ))}
    </div>
  </div>
);

const GoogleFormIntegration = () => {
  const [companyId, setCompanyId] = useState(null);
  const [connected, setConnected] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);

  const [projects, setProjects] = useState([]);
  const [forms, setForms] = useState([]);
  const [loadingForms, setLoadingForms] = useState(false);

  // ── Create-form builder ──────────────────────────────────────────────
  const [showBuilder, setShowBuilder] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [newForm, setNewForm] = useState({
    formTitle: '',
    formDescription: '',
    projectId: '',
    questions: [blankQuestion()],
  });
  const newFormHandlers = createQuestionHandlers(setNewForm);

  // ── Edit-form builder ────────────────────────────────────────────────
  const [showEditor, setShowEditor] = useState(false);
  const [editingConfigId, setEditingConfigId] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState(null);
  const [editForm, setEditForm] = useState({
    formTitle: '',
    formDescription: '',
    projectId: '',
    questions: [blankQuestion()],
  });
  const editFormHandlers = createQuestionHandlers(setEditForm);

  const [actionError, setActionError] = useState(null);
  const [busyConfigId, setBusyConfigId] = useState(null);

  // ── Initial load ──────────────────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Authentication required. Please log in again.');
      setCheckingStatus(false);
      return;
    }
    const id = getCompanyIdFromToken(token);
    if (!id) {
      setError('Invalid session. Could not identify your company from the token. Please log in again.');
      setCheckingStatus(false);
      return;
    }
    setCompanyId(id);
  }, []);

  const refreshStatus = useCallback(async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/google-forms/oauth/status?companyId=${id}`, {
        headers: authHeaders(),
      });
      const data = await res.json();
      setConnected(!!data.connected);
    } catch (err) {
      console.error('Status check failed:', err);
    } finally {
      setCheckingStatus(false);
    }
  }, []);

  const loadProjects = useCallback(async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/projectName/company/${id}`, {
        headers: authHeaders(),
      });
      if (!res.ok) return;
      const data = await res.json();
      setProjects(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load projects:', err);
    }
  }, []);

  const loadForms = useCallback(async (id) => {
    setLoadingForms(true);
    try {
      const res = await fetch(`${API_URL}/api/google-forms/config/${id}`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Failed to load forms');
      const data = await res.json();
      setForms(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingForms(false);
    }
  }, []);

  useEffect(() => {
    if (!companyId) return;
    refreshStatus(companyId);
    loadProjects(companyId);
    loadForms(companyId);
  }, [companyId, refreshStatus, loadProjects, loadForms]);

  // ── OAuth popup ───────────────────────────────────────────────────────
  useEffect(() => {
    const handleMessage = (event) => {
      // SECURITY: only accept messages from our own backend's callback page,
      // opened by us, at the origin we expect.
      if (event.origin !== FRONTEND_ORIGIN && event.origin !== new URL(API_URL).origin) return;
      if (!event.data || typeof event.data !== 'object') return;

      if (event.data.type === 'GOOGLE_AUTH_SUCCESS') {
        setConnecting(false);
        setError(null);
        refreshStatus(companyId);
      } else if (event.data.type === 'GOOGLE_AUTH_FAILED') {
        setConnecting(false);
        setError(event.data.error || 'Authentication failed or was cancelled.');
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [companyId, refreshStatus]);

  const handleConnectGoogle = async () => {
    if (!companyId) return;
    setConnecting(true);
    setError(null);

    try {
      const jwtToken = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/google-forms/oauth/url?companyId=${companyId}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${jwtToken}`, 'Content-Type': 'application/json' },
      });

      if (!response.ok) throw new Error('Failed to initialize Google connection. Please try again.');
      const data = await response.json();
      if (!data.authUrl) throw new Error('Invalid response from the server. Authorization URL is missing.');

      const width = 500, height = 650;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        data.authUrl,
        'GoogleOAuth',
        `width=${width},height=${height},left=${left},top=${top},toolbar=0,scrollbars=1,status=1,resizable=1`
      );

      if (!popup) throw new Error('Popup was blocked by your browser. Please allow popups for this site.');

      const checkPopupClosed = setInterval(() => {
        if (popup && popup.closed) {
          clearInterval(checkPopupClosed);
          setConnecting(false);
        }
      }, 1000);
    } catch (err) {
      console.error('OAuth Initialization Error:', err);
      setError(err.message || 'An unexpected error occurred while connecting to the server.');
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!companyId) return;
    if (!window.confirm('Disconnect your Google account? Existing forms will stop capturing leads until you reconnect.')) return;
    try {
      await fetch(`${API_URL}/api/google-forms/oauth/disconnect?companyId=${companyId}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      setConnected(false);
    } catch (err) {
      setError('Failed to disconnect. Please try again.');
    }
  };

  // ── Create form ───────────────────────────────────────────────────────
  const resetBuilder = () =>
    setNewForm({ formTitle: '', formDescription: '', projectId: '', questions: [blankQuestion()] });

  const handleCreateForm = async () => {
    setCreateError(null);

    if (!newForm.formTitle.trim()) return setCreateError('Form title is required.');
    if (!newForm.projectId) return setCreateError('Please assign this form to a project.');
    if (newForm.questions.some((q) => !q.title.trim())) return setCreateError('Every question needs a title.');

    const project = projects.find((p) => p.id === newForm.projectId);

    const payload = {
      companyId,
      formTitle: newForm.formTitle,
      formDescription: newForm.formDescription,
      projectId: newForm.projectId,
      projectName: project ? project.name : '',
      projectColor: project ? project.color : '',
      questions: serializeQuestions(newForm.questions),
    };

    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/google-forms/create?companyId=${companyId}`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create form.');

      setShowBuilder(false);
      resetBuilder();
      await loadForms(companyId);
    } catch (err) {
      setCreateError(err.message);
    } finally {
      setCreating(false);
    }
  };

  // ── Edit form ─────────────────────────────────────────────────────────
  const openEditForm = async (configId) => {
    setEditError(null);
    setEditingConfigId(configId);
    setShowEditor(true);
    setEditLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/google-forms/${configId}/details?companyId=${companyId}`,
        { headers: authHeaders() }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load form details.');

      setEditForm({
        formTitle: data.title || '',
        formDescription: data.description || '',
        projectId: data.projectId || '',
        questions: deserializeQuestions(data.items, data.questionMappings),
      });
    } catch (err) {
      setEditError(err.message);
    } finally {
      setEditLoading(false);
    }
  };

  const closeEditForm = () => {
    setShowEditor(false);
    setEditingConfigId(null);
    setEditError(null);
    setEditForm({ formTitle: '', formDescription: '', projectId: '', questions: [blankQuestion()] });
  };

  const handleUpdateForm = async () => {
    setEditError(null);

    if (!editForm.formTitle.trim()) return setEditError('Form title is required.');
    if (editForm.questions.some((q) => !q.title.trim())) return setEditError('Every question needs a title.');

    const project = projects.find((p) => p.id === editForm.projectId);

    const payload = {
      formTitle: editForm.formTitle,
      formDescription: editForm.formDescription,
      questions: serializeQuestions(editForm.questions),
      ...(project
        ? { projectId: project.id, projectName: project.name, projectColor: project.color }
        : {}),
    };

    setSaving(true);
    try {
      const res = await fetch(
        `${API_URL}/api/google-forms/${editingConfigId}?companyId=${companyId}`,
        {
          method: 'PUT',
          headers: authHeaders(),
          body: JSON.stringify(payload),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update form.');

      closeEditForm();
      await loadForms(companyId);
    } catch (err) {
      setEditError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Per-form actions ──────────────────────────────────────────────────
  const handleEnableWatch = async (configId) => {
    setActionError(null);
    setBusyConfigId(configId);
    try {
      const res = await fetch(`${API_URL}/api/google-forms/watch/${configId}`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to enable live capture.');
      await loadForms(companyId);
    } catch (err) {
      setActionError(err.message);
    } finally {
      setBusyConfigId(null);
    }
  };

  const handleReassignProject = async (configId, projectId) => {
    const project = projects.find((p) => p.id === projectId);
    if (!project) return;
    setActionError(null);
    setBusyConfigId(configId);
    try {
      const res = await fetch(
        `${API_URL}/api/google-forms/config/${configId}/project?companyId=${companyId}`,
        {
          method: 'PUT',
          headers: authHeaders(),
          body: JSON.stringify({ projectId: project.id, projectName: project.name, projectColor: project.color }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to reassign project.');
      await loadForms(companyId);
    } catch (err) {
      setActionError(err.message);
    } finally {
      setBusyConfigId(null);
    }
  };

  const handleDeleteForm = async (configId) => {
    if (!window.confirm('Remove this form? It will stop capturing leads. The Google Form itself is not deleted.')) return;
    setActionError(null);
    setBusyConfigId(configId);
    try {
      const res = await fetch(
        `${API_URL}/api/google-forms/config/${configId}?companyId=${companyId}`,
        { method: 'DELETE', headers: authHeaders() }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to delete form.');
      await loadForms(companyId);
    } catch (err) {
      setActionError(err.message);
    } finally {
      setBusyConfigId(null);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div className="p-6 max-w-5xl mx-auto font-sans cfg-font">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700&family=Inter:wght@400;500;600&display=swap'); .cfg-heading{font-family:'Sora',sans-serif;} .cfg-font{font-family:'Inter',sans-serif;}`}</style>
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8 mb-6">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-semibold cfg-heading text-gray-800 mb-2">Google Forms Integration</h2>
            <p className="text-gray-600 leading-relaxed">
              Connect your Google account, create and edit forms, assign each one to a project, and
              capture submissions as leads automatically.
            </p>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded-r text-sm">
            <p>{error}</p>
          </div>
        )}

        {checkingStatus ? (
          <p className="text-sm text-gray-500">Checking connection status…</p>
        ) : connected ? (
          <div className="flex items-center gap-4">
            <div className="bg-green-50 border-l-4 border-green-500 text-green-700 px-4 py-2 rounded-r text-sm">
              Connected to Google
            </div>
            <button
              onClick={handleDisconnect}
              className="text-sm text-gray-500 hover:text-red-600 underline"
            >
              Disconnect
            </button>
          </div>
        ) : (
          <button
            onClick={handleConnectGoogle}
            disabled={connecting}
            className={`px-5 py-2.5 text-white rounded-md font-medium transition-all ${
              connecting
                ? 'bg-[#C4B5FD] cursor-not-allowed'
                : 'bg-[#673AB7] hover:bg-[#5E35A6] shadow-sm hover:shadow'
            }`}
          >
            {connecting ? 'Waiting for Google...' : 'Authorize Google Account'}
          </button>
        )}
      </div>

      {connected && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold cfg-heading text-gray-800">Your Forms</h3>
            <button
              onClick={() => setShowBuilder(true)}
              className="px-4 py-2 bg-[#673AB7] hover:bg-[#5E35A6] text-white rounded-md text-sm font-medium"
            >
              + New Form
            </button>
          </div>

          {actionError && (
            <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-3 mb-4 rounded-r text-sm">
              {actionError}
            </div>
          )}

          {loadingForms ? (
            <p className="text-sm text-gray-500">Loading forms…</p>
          ) : forms.length === 0 ? (
            <p className="text-sm text-gray-500">No forms yet. Create one to start capturing leads.</p>
          ) : (
            <div className="space-y-3">
              {forms.map((f) => (
                <div key={f.id} className="border border-gray-200 rounded-md p-4 flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="font-medium text-gray-800">{f.projectName || f.formId}</div>
                    <div className="text-xs text-gray-500 mt-1 flex items-center gap-2 flex-wrap">
                      <span
                        className="inline-block px-2 py-0.5 rounded-full text-white text-xs"
                        style={{ backgroundColor: f.assignedProjectColor || '#6b7280' }}
                      >
                        {f.assignedProjectName || 'No project'}
                      </span>
                      <span>Form ID: {f.formId}</span>
                      {f.watchId ? (
                        <span className="text-green-600">Live capture ON{f.watchExpiry ? ` · renews before ${new Date(f.watchExpiry).toLocaleDateString()}` : ''}</span>
                      ) : (
                        <span className="text-amber-600">Live capture OFF</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => openEditForm(f.id)}
                      disabled={busyConfigId === f.id}
                      className="text-sm px-3 py-1.5 border border-[#673AB7] text-[#673AB7] hover:bg-[#F3E8FF] rounded-md disabled:opacity-50"
                    >
                      Edit
                    </button>

                    <a
                      href={`https://docs.google.com/forms/d/${f.formId}/edit`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm text-[#673AB7] hover:underline"
                    >
                      Open in Google
                    </a>

                    <select
                      className="text-sm border border-gray-300 rounded-md px-2 py-1"
                      value={f.projectId || ''}
                      onChange={(e) => handleReassignProject(f.id, e.target.value)}
                      disabled={busyConfigId === f.id}
                    >
                      <option value="" disabled>Reassign project…</option>
                      {projects.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>

                    {!f.watchId && (
                      <button
                        onClick={() => handleEnableWatch(f.id)}
                        disabled={busyConfigId === f.id}
                        className="text-sm px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-md disabled:opacity-50"
                      >
                        {busyConfigId === f.id ? 'Enabling…' : 'Enable live capture'}
                      </button>
                    )}

                    <button
                      onClick={() => handleDeleteForm(f.id)}
                      disabled={busyConfigId === f.id}
                      className="text-sm px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-md disabled:opacity-50"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Create Form modal ────────────────────────────────────────── */}
      {showBuilder && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <h3 className="text-lg font-semibold cfg-heading text-gray-800 mb-4">Create Google Form</h3>

            {createError && (
              <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-3 mb-4 rounded-r text-sm">
                {createError}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Form title</label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  value={newForm.formTitle}
                  onChange={(e) => setNewForm((f) => ({ ...f, formTitle: e.target.value }))}
                  placeholder="e.g. Site Visit Enquiry"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                <textarea
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  rows={2}
                  value={newForm.formDescription}
                  onChange={(e) => setNewForm((f) => ({ ...f, formDescription: e.target.value }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assign to project</label>
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  value={newForm.projectId}
                  onChange={(e) => setNewForm((f) => ({ ...f, projectId: e.target.value }))}
                >
                  <option value="">Select a project…</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                {projects.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">No projects found. Create a project first.</p>
                )}
              </div>

              <QuestionListEditor questions={newForm.questions} handlers={newFormHandlers} />
            </div>

            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                onClick={() => { setShowBuilder(false); resetBuilder(); }}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateForm}
                disabled={creating}
                className="px-4 py-2 text-sm bg-[#673AB7] hover:bg-[#5E35A6] text-white rounded-md disabled:opacity-50"
              >
                {creating ? 'Creating…' : 'Create Form'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Form modal ──────────────────────────────────────────── */}
      {showEditor && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <h3 className="text-lg font-semibold cfg-heading text-gray-800 mb-4">Edit Google Form</h3>

            {editError && (
              <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-3 mb-4 rounded-r text-sm">
                {editError}
              </div>
            )}

            {editLoading ? (
              <p className="text-sm text-gray-500">Loading current form…</p>
            ) : (
              <>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Form title</label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      value={editForm.formTitle}
                      onChange={(e) => setEditForm((f) => ({ ...f, formTitle: e.target.value }))}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                    <textarea
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      rows={2}
                      value={editForm.formDescription}
                      onChange={(e) => setEditForm((f) => ({ ...f, formDescription: e.target.value }))}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Assigned project</label>
                    <select
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      value={editForm.projectId}
                      onChange={(e) => setEditForm((f) => ({ ...f, projectId: e.target.value }))}
                    >
                      <option value="">Select a project…</option>
                      {projects.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>

                  <QuestionListEditor questions={editForm.questions} handlers={editFormHandlers} />

                  <p className="text-xs text-gray-400">
                    Saving replaces this form's question set on Google (previous questions are removed and
                    recreated), so lead-field mappings are rebuilt to match automatically.
                  </p>
                </div>

                <div className="flex items-center justify-end gap-3 mt-6">
                  <button
                    onClick={closeEditForm}
                    className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-md"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateForm}
                    disabled={saving}
                    className="px-4 py-2 text-sm bg-[#673AB7] hover:bg-[#5E35A6] text-white rounded-md disabled:opacity-50"
                  >
                    {saving ? 'Saving…' : 'Save Changes'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleFormIntegration;